import type { CoreNotifyEvent, JsRequest } from '@/services/websocket/types'
import { WebSocketStatus } from '@/services/websocket/types'
import { useUserStore } from '@/stores/user'
import { useWebSocketStore } from '@/stores/websocket'
import { useAppStore } from '@/stores/app'
import { getWebSocketService } from '@/services/websocket/websocket.service'
import { hasFullSession } from '@/common/plugins/axios'
import { readPlatformSessionIdFromPageEntry } from '@/common/utils/platformFileSession'
import { normalizePlatformSessionId } from '@/common/utils/jsRequestSessionContext'
import { computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'

export function useWebSocket() {
  const wsStore = useWebSocketStore()
  const userStore = useUserStore()

  const status = computed(() => wsStore.status)
  const connected = computed(() => wsStore.connected)
  const connecting = computed(() => wsStore.connecting)
  const error = computed(() => wsStore.error)
  const events = computed(() => wsStore.events)

  function connect() {
    // WebSocket requires a full session (JSESSIONID cookie), not just remember-me token
    if (!hasFullSession()) {
      const errorMessage = 'WebSocket requires a full session. Please log in again to establish a proper session.'
      wsStore.setError(errorMessage)
      return Promise.reject(new Error(errorMessage))
    }

    const token = localStorage.getItem('auth_token')
    return wsStore.connect(token || undefined)
  }

  function disconnect() {
    wsStore.disconnect()
  }

  function onEvent(callback: (event: CoreNotifyEvent) => void) {
    const unwatch = computed(() => wsStore.events[0])

    const stop = () => { }

    return stop
  }

  function onJsRequest(callback: (request: JsRequest) => Promise<any>) {
    const wsService = getWebSocketService()
    return wsService.onJsRequest(callback)
  }

  function onOfficeEvent(callback: (request: JsRequest) => void) {
    const wsService = getWebSocketService()
    return wsService.onOfficeEvent(callback)
  }

  function getSocketId() {
    const wsService = getWebSocketService()
    return wsService.getSocketId()
  }

  async function autoConnect() {
    if (userStore.isAuthenticated && !wsStore.connected && !wsStore.connecting) {
      try {
        await connect()
      } catch (error: any) {
        // Check if error is due to remember-me authentication
        const isRememberMeError = error?.message?.includes('remember-me') ||
          error?.message?.includes('full session') ||
          error?.message?.includes('WebSocket unavailable')

        if (isRememberMeError) {
          wsStore.setError('WebSocket requires full login session')
        }
        throw error
      }
    }
  }

  function autoDisconnect() {
    if (wsStore.connected) {
      disconnect()
    }
  }

  return {
    status,
    connected,
    connecting,
    error,
    events,
    reconnectAttempts: computed(() => wsStore.reconnectAttempts),
    connect,
    disconnect,
    onEvent,
    onJsRequest,
    onOfficeEvent,
    getSocketId,
    autoConnect,
    autoDisconnect,
    sendTaskResult: wsStore.sendTaskResult,
    clearEvents: wsStore.clearEvents
  }
}

export function useWebSocketAutoConnect() {
  const { autoConnect, autoDisconnect, status, connected, connecting } = useWebSocket()
  const userStore = useUserStore()
  const appStore = useAppStore()
  const route = useRoute()
  const wsService = getWebSocketService()
  let sessionCheckInterval: ReturnType<typeof setInterval> | null = null

  const attemptConnection = async () => {
    // Don't try to connect on login/signup pages
    if (route.name === 'signin' || route.name === 'signup') {
      return
    }

    const shouldAttempt = userStore.isAuthenticated && !connected.value && !connecting.value

    if (shouldAttempt) {
      try {
        // Try to connect - the WebSocket service will handle session validation
        await autoConnect()
        // Clear interval if connection succeeds
        if (sessionCheckInterval) {
          clearInterval(sessionCheckInterval)
          sessionCheckInterval = null
        }
      } catch (err: any) {
        // Connection failed - will retry via interval if needed
      }
    }
  }

  // Watch for authentication changes
  watch(
    () => userStore.isAuthenticated,
    async (isAuth) => {
      if (isAuth) {
        // Don't try to connect on login/signup pages
        if (route.name === 'signin' || route.name === 'signup') {
          return
        }

        // Wait a bit for cookies to be fully set after login
        await nextTick()
        await new Promise(resolve => setTimeout(resolve, 500))

        // Try to connect if we have a full session
        if (hasFullSession()) {
          await attemptConnection()
        }

        // Start polling for connection if not connected yet
        // Don't require hasFullSession() since JSESSIONID might be HttpOnly
        if (status.value !== WebSocketStatus.CONNECTED && !sessionCheckInterval) {
          sessionCheckInterval = setInterval(() => {
            // Don't try to connect on login/signup pages
            if (route.name === 'signin' || route.name === 'signup') {
              return
            }
            if (userStore.isAuthenticated && !connected.value && !connecting.value) {
              attemptConnection()
            } else if (connected.value && sessionCheckInterval) {
              clearInterval(sessionCheckInterval)
              sessionCheckInterval = null
            }
          }, 2000) // Check every 2 seconds
        }
      } else {
        // Disconnect when user logs out
        if (sessionCheckInterval) {
          clearInterval(sessionCheckInterval)
          sessionCheckInterval = null
        }
        autoDisconnect()
      }
    },
    { immediate: true }
  )

  // Watch route changes to connect after login
  watch(
    () => route.name,
    (routeName) => {
      // If we navigate away from signin/signup and are authenticated, try to connect
      if (routeName !== 'signin' && routeName !== 'signup' && userStore.isAuthenticated && !connected.value && !connecting.value) {
        attemptConnection()
      }
    }
  )

  // Watch for changes in active forms (core_ids) and update WebSocket subscriptions
  watch(
    () => [appStore.formStackFids, appStore.currentFormFid],
    () => {
      // Update subscriptions when forms are opened/closed
      if (connected.value) {
        wsService.updateSubscriptions()
      }
    },
    { deep: true }
  )

  // Подписываемся на /client/event/app|{form}/{sessionId} сразу после появления core session в store
  watch(
    () => {
      const coreIds = new Set<string>()
      const addEntry = (entry: unknown) => {
        const sid = readPlatformSessionIdFromPageEntry(entry)
        if (sid) coreIds.add(sid)
      }
      Object.values(appStore.pagesData || {}).forEach(addEntry)
      Object.values(appStore.dialogs || {}).forEach(addEntry)
      Object.values(appStore.overlays || {}).forEach(addEntry)
      const current = normalizePlatformSessionId(appStore.currentSessionId)
      if (current) coreIds.add(current)
      return Array.from(coreIds).sort().join('|')
    },
    () => {
      if (connected.value) {
        wsService.updateSubscriptions()
      }
    },
    { flush: 'sync' },
  )

  // Update subscriptions immediately after connection is established
  watch(
    () => status.value,
    (newStatus) => {
      if (newStatus === WebSocketStatus.CONNECTED) {
        // Update subscriptions when WebSocket connects (in case forms are already open)
        wsService.updateSubscriptions()
        void import('@/services/form-events/platformConnection.service').then(
          ({ syncPlatformConnectionFromApi }) => {
            void syncPlatformConnectionFromApi()
          },
        )
      }
    }
  )

  onMounted(() => {
    // Don't try to connect on login/signup pages
    if (route.name === 'signin' || route.name === 'signup') {
      return
    }

    // Try to connect if already authenticated
    // Don't require hasFullSession() since JSESSIONID might be HttpOnly
    if (userStore.isAuthenticated) {
      attemptConnection()

      // Start polling in case connection fails or isn't ready yet
      if (!sessionCheckInterval) {
        sessionCheckInterval = setInterval(() => {
          // Don't try to connect on login/signup pages
          if (route.name === 'signin' || route.name === 'signup') {
            return
          }
          if (userStore.isAuthenticated && !connected.value && !connecting.value) {
            attemptConnection()
          } else if (connected.value && sessionCheckInterval) {
            clearInterval(sessionCheckInterval)
            sessionCheckInterval = null
          }
        }, 2000)
      }
    }
  })

  onUnmounted(() => {
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval)
    }
    autoDisconnect()
  })
}

